import api from './api';

/**
 * HOME — VEHÍCULOS v2 API service (F119–F123, F125) — unit-scoped.
 * Backend: api/src/modules/home/vehicles-v2.routes.js, mounted at /api/home/vehicles
 * via router.use() from vehicles.routes.js (legacy F89/F90 registry routes stay first).
 *
 * Unit scoping follows the homeApi.ts convention (FIX 2026-08-31): every call
 * carries the active unit id explicitly (query param / body unitId) instead of
 * relying on the backend requireUnit auto-resolver; the axios interceptor adds
 * the X-Active-Unit-Id header as a fallback only.
 *
 * F124 (AI receipt extraction) is intentionally NOT wired yet — the service
 * form in HomeVehiclesV2 is structured so a draft-prefill affordance can be
 * added later without reshaping state.
 */
export const vehiclesV2Api = {
  // F119 registry delete (query unitId keeps the active unit explicit).
  registry: {
    delete: (vehicleId: string, unitId?: string) =>
      api.delete(`/home/vehicles/${vehicleId}`, { params: unitId ? { unitId } : {} }),
  },

  // F119: vehicle detail with badge counts.
  detail: (vehicleId: string) => api.get(`/home/vehicles/${vehicleId}`),

  // F120: service history (paginated + q/type filters).
  services: {
    list: (
      vehicleId: string,
      opts?: { page?: number; pageSize?: number; q?: string; type?: string }
    ) =>
      api.get(`/home/vehicles/${vehicleId}/services`, {
        params: {
          ...(opts?.page ? { page: opts.page } : {}),
          ...(opts?.pageSize ? { pageSize: opts.pageSize } : {}),
          ...(opts?.q ? { q: opts.q } : {}),
          ...(opts?.type ? { type: opts.type } : {}),
        },
      }),
    create: (vehicleId: string, data: any, unitId?: string) =>
      api.post(`/home/vehicles/${vehicleId}/services`, { ...data, ...(unitId ? { unitId } : {}) }),
    update: (serviceId: string, data: any) => api.put(`/home/vehicle-services/${serviceId}`, data),
    delete: (serviceId: string) => api.delete(`/home/vehicle-services/${serviceId}`),
  },

  // F121: maintenance schedule rules (next_due recomputed server-side).
  serviceRules: {
    list: (vehicleId: string) => api.get(`/home/vehicles/${vehicleId}/service-rules`),
    update: (vehicleId: string, ruleId: string, data: any) =>
      api.put(`/home/vehicles/${vehicleId}/service-rules/${ruleId}`, data),
    delete: (vehicleId: string, ruleId: string) =>
      api.delete(`/home/vehicles/${vehicleId}/service-rules/${ruleId}`),
  },

  // F122: paperwork + renew (renew creates the successor cycle + a one-time reminder).
  papers: {
    list: (vehicleId: string, status?: 'active' | 'expired' | 'all') =>
      api.get(`/home/vehicles/${vehicleId}/papers`, {
        params: status && status !== 'all' ? { status } : {},
      }),
    create: (vehicleId: string, data: any) => api.post(`/home/vehicles/${vehicleId}/papers`, data),
    update: (paperId: string, data: any) => api.put(`/home/vehicle-papers/${paperId}`, data),
    delete: (paperId: string) => api.delete(`/home/vehicle-papers/${paperId}`),
    renew: (vehicleId: string, paperId: string, data: any) =>
      api.post(`/home/vehicles/${vehicleId}/papers/${paperId}/renew`, data),
  },

  // F123: document vault. Upload sends { docType, file: <base64 data URL>, filename };
  // the API sniffs magic bytes and allowlists application/pdf, image/jpeg, image/png.
  // The file route requires a bearer token, so files are fetched as blobs
  // (plain <a href> links would 401).
  documents: {
    list: (vehicleId: string) => api.get(`/home/vehicles/${vehicleId}/documents`),
    upload: (vehicleId: string, data: { docType: string; file: string; filename?: string }) =>
      api.post(`/home/vehicles/${vehicleId}/documents`, data),
    fetchFile: (documentId: string) =>
      api.get(`/home/vehicle-documents/${documentId}/file`, { responseType: 'blob' }),
    delete: (documentId: string) => api.delete(`/home/vehicle-documents/${documentId}`),
  },

  // F125: reminders + due/overdue computation.
  reminders: {
    list: (unitId?: string, status?: 'overdue' | 'upcoming' | 'all') =>
      api.get('/home/vehicles/reminders', {
        params: {
          ...(unitId ? { unitId } : {}),
          ...(status && status !== 'all' ? { status } : {}),
        },
      }),
    // Computed per-vehicle due/overdue view (papers + rules + reminders, currentKm).
    overdue: (vehicleId: string) => api.get(`/home/vehicles/${vehicleId}/overdue`),
    create: (vehicleId: string, data: any) => api.post(`/home/vehicles/${vehicleId}/reminders`, data),
    // Also handles snooze: { snoozeDays: n } pushes due_date out and returns to 'scheduled'.
    update: (reminderId: string, data: any) => api.put(`/home/vehicle-reminders/${reminderId}`, data),
  },
};

export default vehiclesV2Api;
